import React from 'react';
import {
    Card,
    CardContent,
    CardActions,
    Typography,
    Box,
} from '@mui/material';
import {PLButton} from './PLFields';
import { ErrorOutline } from '@mui/icons-material';
import type { ErrorInfo } from 'react';
import {ErrorDetailsAccordion} from './ErrorDetailsAccordion';
import {AppError, ErrorCategory} from '../../types/errors';

interface ErrorFallbackProps {
    error: Error;
    errorInfo?: ErrorInfo | null;
    onReset: () => void;
}

/**
 * Material-UI-based error fallback component for Error Boundary.
 * Displays user-friendly error message with collapsible technical details.
 * 
 * User decisions from CONTEXT.md:
 * - Always show friendly message
 * - Technical details in collapsible Accordion (using ErrorDetailsAccordion)
 * - Include timestamp for support tickets
 * - Material-UI Card matches existing app design patterns
 */
const ErrorFallback: React.FC<ErrorFallbackProps> = ({
    error,
    errorInfo,
    onReset,
}) => {
    // Construct AppError object from ErrorBoundary state
    const appError: AppError = {
        category: ErrorCategory.Client,  // Rendering errors are client-side
        message: 'Something went wrong. Please reload the page.',
        originalError: error,  // The Error object from componentDidCatch
        timestamp: new Date(),
        isRetryable: true,
        retryCount: 0,
        source: errorInfo?.componentStack 
            ? errorInfo.componentStack.split('\n')[1]?.trim().slice(0, 200) || 'Unknown component'
            : 'Unknown component'
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 400,
                padding: 2,
            }}
        >
            <Card sx={{ maxWidth: 600, width: '100%' }}>
                <CardContent>
                    {/* Error icon and heading */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <ErrorOutline
                            color="error"
                            sx={{ fontSize: 40, mr: 2 }}
                        />
                        <Typography variant="h5" component="h2">
                            Something went wrong
                        </Typography>
                    </Box>

                    {/* User-friendly message */}
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        We encountered an unexpected error. Please try
                        refreshing the page or contact support if the problem
                        persists.
                    </Typography>

                    {/* Collapsible technical details using ErrorDetailsAccordion */}
                    <ErrorDetailsAccordion error={appError} />
                </CardContent>

                {/* Retry button */}
                <CardActions>
                    <PLButton
                        variant="contained"
                        onClick={onReset}
                        fullWidth
                    >
                        Retry
                    </PLButton>
                </CardActions>
            </Card>
        </Box>
    );
};

export default ErrorFallback;
